import { cn } from '../../lib/utils.ts';

/** Anteil auf 0–1 begrenzen; ungültige Werte (NaN, ±∞) zählen als 0. */
export function clampShare(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Kapselbalken (DESIGN „Design S3“ › Formen, Change 11.10.2026): Balken außerhalb von Kennzahl-Kacheln (Ziel unter „Aufträge
 * heute“, Kanäle, Versandfluss, Lager, Top-Listen; Kennzahl-Kacheln zeigen seit Ticket 211 Mini-Säulen und das Ziel als
 * Text, Rudolf 15.10.2026) – 6 px hoch, ganz rund,
 * Spur hellgrau, Füllung TILL-Dunkelblau (dunkel Hellblau mit heller Zielmarke), optional eine kleine Zielmarke. Je Seite nur eine Grafik-Art (Rudolf
 * 10.10.2026: „entweder Charts oder Balken“). `value` und `target` sind Anteile 0–1 (z. B. 0,58 = 58 % vom Ziel);
 * die Zahl daneben schreibt die Kachel selbst, der Balken trägt sie für Hilfsmittel als `aria-valuetext`.
 */
/** Füllung je Ton: Akzent (Standard) oder Status-Schrift (Längenbalken, Story 5.103: zu kurz/gut/zu lang). */
export type CapsuleTone = 'accent' | 'ready' | 'check' | 'clarify';
const CAPSULE_FILL: Readonly<Record<CapsuleTone, string>> = { accent: 'bg-capsule', ready: 'bg-ready', check: 'bg-check', clarify: 'bg-clarify' };

export function CapsuleBar({
  value,
  target,
  label,
  valueText,
  tone = 'accent',
  className,
}: {
  value: number;
  target?: number;
  /** Name für Hilfsmittel, z. B. „Umsatz Februar, Anteil am Monatsziel“. */
  label: string;
  /** Vorgelesener Wert, z. B. „58 % vom Monatsziel, Soll 57 %“. */
  valueText: string;
  tone?: CapsuleTone;
  className?: string;
}) {
  const share = clampShare(value);
  const goal = target === undefined ? undefined : clampShare(target);
  return (
    <span
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(share * 100)}
      aria-valuetext={valueText}
      data-capsule-bar=""
      className={cn('relative block h-capsule rounded-full bg-line', className)}
    >
      <span
        aria-hidden
        data-capsule-fill={tone}
        className={cn('absolute inset-y-0 left-0 rounded-full transition-[width,background-color] duration-200 ease-out motion-reduce:transition-none', CAPSULE_FILL[tone])}
        style={{ width: `${share * 100}%` }}
      />
      {goal === undefined ? null : (
        <span
          aria-hidden
          data-capsule-target=""
          className="absolute -top-[3px] h-3 w-0.5 rounded-bar bg-capsule-mark ring-2 ring-[var(--s3-capsule-gap)]"
          style={{ left: `calc(${goal * 100}% - 1px)` }}
        />
      )}
    </span>
  );
}
